import { useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Activity, BarChart3, DollarSign, RefreshCw, TrendingUp, Zap,
} from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { WsStatusDot } from '@/components/shared/WsStatusDot';
import { GovernanceAttestationPanel } from '@/components/shared/GovernanceAttestationPanel';
import { formatMoneyString, sumMoneyStrings, PROTOCOL_COLORS } from '@/lib/utils';
import type { AgentQuery, QueryOutcome, WsStatus } from '@/types/app';

const OUTCOME_VARIANT: Record<QueryOutcome, 'success' | 'acp' | 'danger'> = {
  PURCHASED: 'success',
  MATCHED:   'acp',
  REJECTED:  'danger',
};

export const OverviewPage = ({
  agentQueries, isLiveFeedActive, onToggleFeed, wsStatus,
}: {
  agentQueries:     AgentQuery[];
  isLiveFeedActive: boolean;
  onToggleFeed:     () => void;
  wsStatus:         WsStatus;
}) => {
  const stats = useMemo(() => {
    const purchased = agentQueries.filter((q) => q.outcome === 'PURCHASED');
    const matched   = agentQueries.filter((q) => q.outcome !== 'REJECTED');
    // amount is a decimal STRING — sum via integer-cents math, never a float.
    const revenue   = sumMoneyStrings(purchased.map((q) => q.amount));
    const matchRate = agentQueries.length
      ? Math.round((matched.length / agentQueries.length) * 100) : 0;
    // latencyMs may be null (pre-enrichment rows); count only rows that report it.
    const timed     = agentQueries.filter((q): q is typeof q & { latencyMs: number } => q.latencyMs != null);
    const avgLatency = timed.length
      ? Math.round(timed.reduce((sum, q) => sum + q.latencyMs, 0) / timed.length) : 0;
    return { total: agentQueries.length, revenue, matchRate, purchases: purchased.length, avgLatency };
  }, [agentQueries]);

  const recentQueries = agentQueries.slice(0, 12);

  return (
    <div className="max-w-6xl space-y-6">
      {/* Header row */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-zinc-400 text-sm">Real-time agent commerce activity</p>
        </div>
        <div className="flex items-center gap-3">
          <WsStatusDot status={wsStatus} />
          <Button variant="outline" size="sm" onClick={onToggleFeed}>
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${isLiveFeedActive ? 'animate-spin' : ''}`} />
            {isLiveFeedActive ? 'Pause' : 'Resume'}
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { icon: Activity,    label: 'Total Queries',    value: stats.total.toLocaleString(),   color: 'text-violet-400'  },
          { icon: DollarSign,  label: 'Revenue',          value: formatMoneyString(stats.revenue), color: 'text-emerald-400' },
          { icon: TrendingUp,  label: 'Match Rate',       value: `${stats.matchRate}%`,          color: 'text-blue-400'    },
          { icon: Zap,         label: 'Avg Latency',      value: `${stats.avgLatency}ms`,        color: 'text-amber-400'   },
        ].map(({ icon: Icon, label, value, color }) => (
          <Card key={label} className="p-5">
            <Icon className={`w-5 h-5 mb-3 ${color}`} />
            <p className="text-xs text-zinc-500 mb-1">{label}</p>
            <p className="text-2xl font-bold font-mono">{value}</p>
          </Card>
        ))}
      </div>

      {/* v3.0.0 — Constitutional Commerce Attestation Panel (P0) */}
      <GovernanceAttestationPanel />

      {/* Live Query Feed */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <Card>
            <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800">
              <h2 className="font-semibold text-sm flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                Live Agent Feed
              </h2>
              <span className="text-xs text-zinc-500">{agentQueries.length} events</span>
            </div>
            <div className="divide-y divide-zinc-800/50 max-h-[480px] overflow-y-auto">
              <AnimatePresence initial={false}>
                {recentQueries.map((q) => (
                  <motion.div key={q.id}
                    initial={{ opacity: 0, backgroundColor: 'rgba(139,92,246,0.08)' }}
                    animate={{ opacity: 1, backgroundColor: 'rgba(0,0,0,0)' }}
                    transition={{ duration: 1.2 }}
                    className="px-5 py-3 hover:bg-zinc-800/20 transition-colors"
                  >
                    <div className="flex items-center justify-between gap-3 mb-1">
                      <span className="font-mono text-xs font-semibold"
                        style={{ color: PROTOCOL_COLORS[q.protocol] }}>
                        {q.protocol}
                      </span>
                      <Badge variant={OUTCOME_VARIANT[q.outcome]} className="text-[10px] px-1.5 py-0">
                        {q.outcome}
                      </Badge>
                      {/* v3.0.0: show algorand_x402 settlement indicator */}
                      {q.settlementRail === 'algorand_x402' && (
                        <Badge variant="success" className="text-[10px] px-1.5 py-0 gap-1">
                          <span className="w-1 h-1 rounded-full bg-emerald-400" />
                          ALGO
                        </Badge>
                      )}
                      <span className="text-xs text-zinc-600 font-mono ml-auto">
                        {q.latencyMs != null ? `${q.latencyMs}ms` : '—'}
                      </span>
                    </div>
                    <p className="text-sm text-zinc-300 truncate">{q.queryIntent}</p>
                    {q.productMatched && (
                      <p className="text-xs text-zinc-500 truncate mt-0.5">→ {q.productMatched}</p>
                    )}
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-[10px] font-mono text-zinc-600 truncate max-w-[160px]">
                        {q.agentId}
                      </span>
                      {q.amount != null && (
                        <span className="text-xs font-mono text-emerald-400">
                          {formatMoneyString(q.amount)}
                        </span>
                      )}
                    </div>
                    {/* v3.0.0: show governance_hash truncated for attested transactions */}
                    {q.governanceHash && (
                      <div className="mt-1 flex items-center gap-1.5">
                        <span className="text-[9px] text-zinc-700 uppercase tracking-wider">hash</span>
                        <code className="text-[9px] font-mono text-zinc-600 truncate max-w-[200px]">
                          {q.governanceHash.slice(0, 16)}…
                        </code>
                      </div>
                    )}
                  </motion.div>
                ))}
              </AnimatePresence>
              {recentQueries.length === 0 && (
                <div className="flex flex-col items-center justify-center py-16 text-zinc-700">
                  <Activity className="w-8 h-8 mb-3 opacity-40" />
                  <p className="text-sm">Waiting for agent activity…</p>
                </div>
              )}
            </div>
          </Card>
        </div>

        {/* Right Panel */}
        <div className="space-y-4">
          {/* Protocol Breakdown */}
          <Card className="p-5">
            <h3 className="font-semibold text-sm mb-4 flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-violet-400" />
              Protocol Breakdown
            </h3>
            <div className="space-y-3">
              {(['A2A', 'ACP', 'UCP', 'NLIP'] as const).map((proto) => {
                const count = agentQueries.filter((q) => q.protocol === proto).length;
                const pct   = agentQueries.length
                  ? Math.round((count / agentQueries.length) * 100) : 0;
                return (
                  <div key={proto}>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="font-mono font-semibold"
                        style={{ color: PROTOCOL_COLORS[proto] }}>{proto}</span>
                      <span className="text-zinc-400">{count} · {pct}%</span>
                    </div>
                    <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                      <motion.div
                        className="h-full rounded-full"
                        style={{ backgroundColor: PROTOCOL_COLORS[proto] }}
                        initial={{ width: 0 }}
                        animate={{ width: `${pct}%` }}
                        transition={{ duration: 0.6, ease: 'easeOut' }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>

          {/* Recent Purchases */}
          <Card className="p-5">
            <h3 className="font-semibold text-sm mb-4">Recent Purchases</h3>
            <div className="space-y-3">
              {agentQueries
                .filter((q) => q.outcome === 'PURCHASED')
                .slice(0, 5)
                .map((q) => (
                  <div key={q.id} className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-xs text-zinc-300 truncate">{q.productMatched}</p>
                      <p className="text-[10px] text-zinc-600 font-mono truncate">{q.agentId}</p>
                    </div>
                    <div className="shrink-0 text-right">
                      <p className="text-xs font-mono text-emerald-400">{formatMoneyString(q.amount ?? '0')}</p>
                      {q.settlementRail === 'algorand_x402' && (
                        <p className="text-[9px] text-violet-400 font-mono">ALGO x402</p>
                      )}
                    </div>
                  </div>
                ))}
              {agentQueries.filter((q) => q.outcome === 'PURCHASED').length === 0 && (
                <p className="text-xs text-zinc-600 text-center py-4">No purchases yet</p>
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};
